import type { FC } from 'react';
import React, { useContext } from 'react';
import { styled, css } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import {
	ContentIcon,
	QuickActions,
	Subtitle,
	LastAction,
	StatusLozenge,
	Title,
	PageLink,
	PageCardContainer,
	Card,
} from '../Items';
import { PageCardsContext } from '../PageCardsContext';

const listCardStyle = css({
	minWidth: '0',
	padding: `${token('space.100')} ${token('space.200')}`,
	display: 'grid',
	alignItems: 'center',
});

const gridTemplateColumnsMap = {
	FULL: '32px minmax(300px, 2fr) 1fr 1fr 128px',
	NO_LAST_ACTION: '32px minmax(300px, 2fr) 1fr 0fr 128px',
	NO_SUBTITLE: '32px minmax(300px, 2fr) 0fr 0fr 128px',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledCard = styled(Card)({
	boxShadow: 'none',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderBottom: 'none',
	borderRadius: 0,
	'&:hover': {
		boxShadow: 'none',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		'--secondary-text-color': token('color.text.subtle'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentIconContainer = styled.div({
	marginRight: token('space.200'),
	height: '16px',
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RoundedCornerContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	ListCard: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:first-of-type': {
			'border-top-left-radius': token('radius.large'),
			'border-top-right-radius': token('radius.large'),
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-empty-styled-expression -- Ignored via go/DSP-18766
const EmojiWrapper = styled.span({});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleText = styled.div({
	marginRight: token('space.100'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const statusContainerStyles = xcss({
	marginRight: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubtitleContainer = styled.div({
	overflow: 'hidden',
	marginRight: token('space.100'),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LastActionContainer = styled.div({
	overflow: 'hidden',
	marginRight: token('space.100'),
});

export const CompactListView: FC = () => {
	// ⚠️ WARNING: Do you need to add conditional logic? Please refer to README#Views ⚠️
	const { width } = useContext(PageCardsContext);
	let breakPoint = 'FULL';
	if (width < 700) {
		breakPoint = 'NO_SUBTITLE';
	} else if (width < 900) {
		breakPoint = 'NO_LAST_ACTION';
	}

	return (
		<RoundedCornerContainer>
			<PageCardContainer StyledCard={StyledCard} isListitem>
				<PageLink compact />
				<div
					css={listCardStyle}
					style={{ gridTemplateColumns: gridTemplateColumnsMap[breakPoint] }}
					data-testid="page-card-compact-list-view"
				>
					<ContentIconContainer>
						<ContentIcon wrapper={EmojiWrapper} />
					</ContentIconContainer>
					<TitleContainer>
						<TitleText>
							<Title singleLine />
						</TitleText>
						<Box xcss={statusContainerStyles}>
							<StatusLozenge />
						</Box>
					</TitleContainer>
					<SubtitleContainer>
						<Subtitle />
					</SubtitleContainer>
					<LastActionContainer>
						<LastAction />
					</LastActionContainer>
					<QuickActions hidden compact />
				</div>
			</PageCardContainer>
		</RoundedCornerContainer>
	);
};
